import { PlusOutlined } from '@ant-design/icons'
import { PermissionActionEnum, type ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import { setShareId, useAppDispatch } from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  convertSheetConfigToFormConfig,
  CORE_CONSTANT,
  type ViewConfigSheet,
} from '@salescore/core'
import { App, Button, Empty, Modal } from 'antd'
import { t } from 'i18next'
import { type ReactElement, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { useSetChildViewId } from '../hooks/useSetChildViewId'
import {
  type DrillDownParameter,
  type DrillDownParameterForAny,
  type DrillDownParameterForKpiPivot,
  type DrillDownParameterForSheet,
  kpiParameterAtom,
  kpiPivotPickedPresetNameAtom,
} from '../recoil/navigation/atoms'
import { useNavigationModal } from '../recoil/navigation/hooks'
import { useRefetchKpiPivotQueryResult } from '../recoil/records/mutations/useRefetchKpiPivotQueryResult'
import {
  buildConvertedSheetSheetCustomModels,
  kpiFormModifiedAtom,
  sheetCustomModelsAtom,
  viewsContextAtom,
} from '../recoil/view/atoms'
import { useSearchView } from '../recoil/view/selectors/viewsRelatedSelector'
import { configAtom } from '../recoil/viewConfig'
import { DurationTimerStart } from './misc/DurationTimer'
import { ViewComponent } from './ViewComponent'
import { ViewComponentWrapper } from './ViewComponentWrapper'
import { getDimensionsWithValue, ViewDrillDownModalContentForKpi } from './ViewDrillDownModalContentForKpi'

export const ViewDrillDownModal = ({
  onFinish,
  compiledConfig,
  view,
}: {
  onFinish: () => Promise<void> | void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
}): ReactElement => {
  const { drillDownModal } = useNavigationModal()
  const refetchKpiPivotQueryResult = useRefetchKpiPivotQueryResult(compiledConfig, view)
  const {
    isDrillDown,
    isChangedDrillDownModal,
    setIsChangedDrillDownModal,
    isSaveConfigButtonActive,
    setIsSaveConfigButtonActive,
  } = useRecoilValue(viewsContextAtom)
  const [formModified, setFormModified] = useRecoilState(kpiFormModifiedAtom)
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  return (
    <Modal
      // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- ant-modal-without-padding は antd.less にスタイル定義あり
      className="ant-modal-without-padding"
      open={drillDownModal.isModalVisible}
      onCancel={() => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (isChangedDrillDownModal) {
          refetchKpiPivotQueryResult.refetch(false)
        }
        if (
          formModified || // KPI設定パネルで変更があるとき
          (!view.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX) && drillDownModal.innerState.hasChanges) || // KPIシートのレコードに変更があるとき（ただし、KPIプレビューからモーダルを開いた時を除く）
          isSaveConfigButtonActive === true // KPIシートの設定に変更があるとき
        ) {
          const messageKey = `close-modal-warning`
          message.warning({
            key: messageKey,
            content: (
              <span>
                {t(`変更差分がある場合は閉じられません。`)}
                <br />
                {t(`差分を削除してモーダルを閉じますか？`)}
                <Button
                  className="ml-2"
                  onClick={() => {
                    setFormModified(false)
                    setIsSaveConfigButtonActive?.(false)
                    drillDownModal.hideModal()
                    dispatch(setShareId(undefined))
                    message.destroy(messageKey)

                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (!isDrillDown && setIsChangedDrillDownModal !== undefined) {
                      setIsChangedDrillDownModal(false)
                    }
                  }}
                >
                  {t(`はい`)}
                </Button>
              </span>
            ),
            duration: 3,
          })
        } else {
          drillDownModal.hideModal()
          dispatch(setShareId(undefined))

          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (!isDrillDown && setIsChangedDrillDownModal !== undefined) {
            setIsChangedDrillDownModal(false)
          }
        }
      }}
      width={'98%'}
      cancelText={t(`閉じる`)}
      okButtonProps={{ style: { display: 'none' } }}
      style={{ top: '3%' }}
      footer={<></>}
      styles={{
        body: {
          padding: 0,
        },
      }}
      destroyOnHidden
    >
      {drillDownModal.content !== undefined && (
        <ViewDrillDownModalBody
          parameter={drillDownModal.content}
          onFinish={async () => {
            await onFinish()
            drillDownModal.hideModal()
          }}
          view={view}
        />
      )}
    </Modal>
  )
}

const ViewDrillDownModalBody = ({
  parameter,
  onFinish,
  view,
}: {
  parameter: DrillDownParameter
  onFinish: () => Promise<void>
  view: ViewFieldsFragment
}): ReactElement => {
  const previousConfig = useRecoilValue(configAtom)

  const { type } = parameter
  switch (type) {
    case 'form': {
      if (previousConfig.type === 'sheet') {
        return (
          <div data-e2e={`view-drill-down-component-for-${previousConfig.type}`}>
            <ViewDrillDownComponentForSheet
              previousConfig={previousConfig}
              parameter={parameter}
              onFinish={onFinish}
              view={view}
            />
          </div>
        )
      }
      break
    }
    case 'kpi': {
      if (previousConfig.type === 'kpiPivot') {
        return (
          <div data-e2e={`view-drill-down-component-for-${previousConfig.type}`}>
            <ViewDrillDownComponentForKpiPivot parameter={parameter} onFinish={onFinish} view={view} />
          </div>
        )
      }
      if (previousConfig.type === 'kpi') {
        return (
          <div data-e2e={`view-drill-down-component-for-${previousConfig.type}`}>
            <ViewDrillDownModalContentForKpi
              parameter={parameter}
              onFinish={onFinish}
              kpiView={{ ...view, config: previousConfig }}
              view={view}
            />
          </div>
        )
      }
      break
    }
    case 'any': {
      return (
        <div data-e2e={`view-drill-down-component-for-${previousConfig.type}`}>
          <ViewDrillDownComponentForAny parameter={parameter} onFinish={onFinish} view={view} />
        </div>
      )
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      throw new Error(type satisfies never)
    }
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO
  return <Empty />
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO
export const dynamicViewFields: ViewFieldsFragment = {
  id: '',
  viewGroupId: ``,
  type: `sheet`,
  name: ``,
  config: { type: `sheet` },
  rank: 0,
  defaultPermission: PermissionActionEnum.None,
  importedSourceId: ``,
  emoji: ``,
  roleRecordPermissions: [],
  private: false,
  archived: false,
  archivedAt: null,
}

const ViewDrillDownComponentForSheet = ({
  parameter,
  previousConfig,
  onFinish,
  view,
}: {
  parameter: DrillDownParameterForSheet
  previousConfig: ViewConfigSheet
  onFinish: () => Promise<void>
  view: ViewFieldsFragment
}): ReactElement => {
  const { organization } = useMe()
  const sheetCustomModels = useRecoilValue(sheetCustomModelsAtom)
  const convertedSheetCustomModels = useMemo(
    () => buildConvertedSheetSheetCustomModels(sheetCustomModels),
    [sheetCustomModels],
  )
  const formConfig = convertSheetConfigToFormConfig(previousConfig)
  const viewsContext = useRecoilValue(viewsContextAtom)
  const drillDownViewId = CORE_CONSTANT.SHEET_FORM_DYNAMIC_VIEW_ID_PREFIX + view.id
  useSetChildViewId(drillDownViewId, view)
  if (formConfig === undefined) {
    return (
      <Empty
        description={
          <div>
            {t(`ビューが見つかりません。`)}
            <br />
            <Button icon={<PlusOutlined />} type="primary">
              {t(`新規作成`)}
            </Button>
          </div>
        }
      />
    )
  }

  return (
    <div className="pt-6">
      <ViewComponent
        view={{
          ...dynamicViewFields,
          id: drillDownViewId,
          name: `${view.name}のフォーム`,
          config: formConfig,
        }}
        parentAbility={undefined}
        parameter={{ recordId: parameter.recordId }}
        onFinish={() => {
          void onFinish()
        }}
        viewsContext={{
          ...viewsContext,
          isDrillDown: true,
          models: [
            ...viewsContext.models,
            // 本来であればsheetCustomModelsの解決はViewComponent側で行われるので、ここで渡す必要はないのだが
            // sheetCustomModelsはviewに紐づくモデルであり、上記で動的にフォーム用のviewを生成している影響で、
            // フォーム用のビューには元のシートのsheetCustomModelが紐づかず、解決されない
            // よって、ややこしい対応になってしまうが、ここで元のsheetCustomModelsを渡す
            ...convertedSheetCustomModels,
          ],
        }}
      />
      <DurationTimerStart viewId={drillDownViewId} organizationId={organization.id} trigger="drillDownModalOpened" />
    </div>
  )
  // })
}

//
// parameterからkpiIdを抽出し、kpiViewに変換する
//

// eslint-disable-next-line sonarjs/prefer-read-only-props
function ViewDrillDownComponentForKpiPivot({
  parameter,
  onFinish,
  view,
}: {
  parameter: DrillDownParameterForKpiPivot
  onFinish: () => Promise<void>
  view: ViewFieldsFragment
}): ReactElement {
  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const searchView = useSearchView()
  const dimensionsWithValue = getDimensionsWithValue(parameter, kpiPivotParameter.pivot)
  const kpiDimension = dimensionsWithValue.find((x) => x.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
  const kpiViewId = (kpiDimension?.value?.value as string) ?? parameter.viewId
  const kpiView = searchView(kpiViewId)
  const kpiViewConfig = kpiView?.config
  const [kpiPivotPresetName] = useRecoilState(kpiPivotPickedPresetNameAtom)
  if (kpiView === undefined || kpiViewConfig?.type !== 'kpi') {
    return <Empty description={<div>{t(`KPIビューが見つかりません。`)}</div>} />
  }

  // 前半のロジックが冗長だが、一旦許容する
  return (
    <ViewDrillDownModalContentForKpi
      kpiView={{ ...kpiView, config: kpiViewConfig }}
      parameter={{ ...parameter, kpiPivotPresetName }}
      onFinish={onFinish}
      view={view}
    />
  )
}

const ViewDrillDownComponentForAny = ({
  parameter,
  onFinish,
  view,
}: {
  parameter: DrillDownParameterForAny
  onFinish: () => Promise<void>
  view: ViewFieldsFragment
}): ReactElement => {
  const viewsContext = useRecoilValue(viewsContextAtom)
  useSetChildViewId(parameter.viewId, view)
  return (
    <div
      style={{
        height: 'calc(100vh - 150px)', // 画面一杯に表示するために、100vhからモーダル・タイトルをざっくり引いた値を指定
      }}
    >
      <ViewComponentWrapper
        viewId={parameter.viewId}
        onFinish={() => {
          void onFinish()
        }}
        viewsContext={{ ...viewsContext, isDrillDown: true }}
        withHeader
      />
    </div>
  )
}
